<template>
  <div class="inner">
    <div class="tw:flex tw:justify-between">
      <h1>AM Best Ratings</h1>
      <div style="width: 20rem;">
        <v-text-field density="compact" clearable autofocus variant="outlined" hide-details
          placeholder="Search"
          v-model="searchText"
        >
        </v-text-field>
      </div>
    </div>
    <div class="tw:flex tw:justify-between tw:items-center">
      <FilterBar class="filter-bar" v-if="!loading">
        <CarrierAttributeFilter v-model="selectedInsurers" displayName="Carriers" :items="insurerGroups" @change="applyFilters" data-test="carriers-filter-btn"/>
        <CarrierAttributeFilter v-model="selectedCarrierSizes" displayName="Carrier Size" :items="carrierSizes" @change="applyFilters" data-test="carrier-size-filter-btn"/>
        <CarrierAttributeFilter v-model="selectedCarrierStrengths" displayName="Financial Strength" :items="carrierStrengthsTop" :secondSet="carrierStrengthsBottom" @change="applyFilters" data-test="carrier-strength-filter-btn" />
        <download-button @click="downloadTable"/>
      </FilterBar>

      <AppButton v-if="isAdmin" @click="showUploadModal = true">
        <Upload :size="16" class="tw:stroke-primary" />
        Upload
      </AppButton>
    </div>

    <AppModal :show="showUploadModal" title="Upload AM Best Ratings">
      <template #content>
        <input type="file"
               class="tw:file-input tw:file-input-bordered tw:border-solid tw:w-full"
               :class="{ 'tw:file-input-disabled': uploading, 'tw:hidden': uploadSuccess }"
               ref="fileUpload"
               @change="uploadFileChanged"
        />

        <div v-if="uploadSuccess === false" class="tw:mt-4">
          <div class="tw:flex tw:gap-1 tw:pb-1">
            <AlertCircle class="tw:text-error"/>
            <h3 class="tw:text-red-500">Upload Failed</h3>
          </div>

          <ul v-if="uploadErrors.length > 0" class="tw:list-disc tw:list-inside tw:text-red-500">
            <li v-for="error in uploadErrors" :key="error">{{ error }}</li>
          </ul>
          <p v-else class="tw:pt-1">
            The file upload failed for an unspecified reason. Ensure the file is UTF-8 encoded.
            Special characters may cause an error while processing.
          </p>
        </div>

        <div v-else-if="uploadSuccess === true" class="tw:mt-4">
          <div class="tw:flex tw:gap-1 tw:pb-1">
            <CheckCircle class="tw:text-success"/>
            <h3 class="tw:text-green-500">Upload Successful</h3>
          </div>
        </div>

        <div v-if="uploading" class="tw:mt-4 tw:flex tw:gap-1">
          <span class="tw:loading tw:loading-spinner tw:loading-md"></span>
          <h3>Uploading...</h3>
        </div>
      </template>

      <template #actions>
        <template v-if="uploadSuccess">
          <button class="tw:btn tw:btn-sm tw:btn-primary" @click="dismissUpload">Close</button>
        </template>
        <template v-else>
          <button class="tw:btn tw:btn-sm" @click="dismissUpload">Cancel</button>
          <button class="tw:btn tw:btn-sm tw:btn-primary tw:text-white"
                  :class="{ 'tw:btn-disabled': !isFileSelectedForUpload } "
                  @click="confirmUpload"
          >
            Upload
          </button>
        </template>
      </template>

    </AppModal>

    <table v-tablesort class="dense">
      <thead>
      <tr>
        <td class="nosort">Issuing Company</td>
        <td class="nosort">AM Best Rating</td>
        <td class="nosort">Size Category</td>
        <td class="nosort">Rating Effective Date</td>
        <td class="nosort">Previous Rating</td>
      </tr>
      </thead>
      <tbody>
      <template v-for="group in groupedCarriers" :key="group.groupName">
        <tr >
          <td colspan="5" class="tw:font-bold tw:text-sm tw:text-gray-500">{{ group.groupName }}</td>
        </tr>
        <tr v-for="carrier in group.carriers" :key="carrier.insurerGroup">
          <td class="tw:pl-6"> {{ carrier.issuingCompanyName }}</td>
          <td class="tw:pl-6"> {{ carrier.financialStrengthRating }}</td>
          <td class="tw:pl-6"> {{ carrier.sizeCategory }}</td>
          <td class="tw:pl-6"> {{ formatDate(carrier.ratingEffectiveDate) }}</td>
          <td class="tw:pl-6"> {{ carrier.previousRating }}</td>
        </tr>
      </template>
      </tbody>
    </table>

  </div>
</template>

<script lang="ts">
import { carrierService } from '@/services/api';
import { CarrierRating, CarrierGroup } from '@/types/Carrier';
import { DownloadCSV } from '@/utilities/Download';
import FilterBar from '@/components/FilterBar.vue';
import CarrierAttributeFilter from '@/components/CarrierAttributeFilter.vue';
import { romanToArabic } from '@/utilities/Number';
import DownloadButton from '@/components/v2/DownloadButton.vue';
import { DateTime } from 'luxon';
import AppModal from '@/components/v2/AppModal.vue';
import AppButton from '@/components/v2/AppButton.vue';
import { Upload, AlertCircle, CheckCircle } from 'lucide-vue-next';
import { IsUserAnAdmin } from '@/utilities/AuthUtils';
import store from '@/store';

interface GroupedCarriers {
  groupName: string;
  carriers: CarrierRating[]
}

export default {
  name: 'AMBestRatings',
  components: { Upload, AlertCircle, CheckCircle, AppButton, AppModal, FilterBar, CarrierAttributeFilter, DownloadButton },
  data: () => ({
    loading: true,
    ratings: [] as CarrierGroup[],
    carriers: [] as CarrierRating[],
    carrierSizes: [] as (string | undefined | null)[],
    carrierStrengthsTop: [] as (string | undefined | null)[],
    carrierStrengthsBottom: [] as (string | undefined | null)[],
    insurerGroups: [] as (string | undefined | null)[],
    searchText: '',
    appliedCarriers: [] as (string | undefined)[],
    selectedInsurers: [] as (string | undefined)[],
    selectedCarrierSizes: [] as (string | null | undefined)[],
    selectedCarrierStrengths: [] as (string | null | undefined)[],
    showUploadModal: false,
    uploadErrors: [] as string[],
    uploadSuccess: null as boolean | null,
    uploading: false,
    uploadFileName: '',
  }),
  methods: {
    async downloadTable() {
      const fieldList = ['Carrier Group', 'Issuing Company', 'AM Best Rating', 'Size Category', 'Rating Effective Date', 'Previous Rating'].join(',');
      const data = this.carriers
          .sort((c1, c2) => `${c1.insurerGroup}${c1.financialStrengthRating}`.localeCompare(`${c2.insurerGroup}${c2.financialStrengthRating}`))
          .map(c => `"${c.insurerGroup}","${c.issuingCompanyName}","${c.financialStrengthRating || ''}","${c.sizeCategory || ''}","${this.formatDate(c.ratingEffectiveDate)}","${c.previousRating || ''}"`).join('\n');
      DownloadCSV(`${fieldList}\n${data}`, 'AM Best Ratings');
    },
    async refreshData(): Promise<void> {
      this.ratings = await carrierService.amBestRatings();

      this.hydrateCarriers();

      this.carrierSizes = Array.from(new Set(this.ratings.flatMap((x: CarrierGroup) => (x.carriers.map(c => c.sizeCategory))).sort((a, b) => romanToArabic(a) - romanToArabic(b)).filter(x => x)));
      
      const strengthOrder = ['A++', 'A+',  'A', 'A-', 'A- u', 'B++', 'B+', 'B', 'B-', 'C++', 'C+', 'C', 'C-', 'D', 'E', 'F', 'NR', 'S'];
      let carrierStrengths = Array.from(new Set(this.ratings.flatMap((x: CarrierGroup) => (x.carriers.map(c => c.financialStrengthRating))).filter(x => x)));
      carrierStrengths = strengthOrder.filter(x => carrierStrengths.includes(x));
      //divide the carrier strengths into top and bottom halfs to be fed into the attribute filter component and display as intended
      this.carrierStrengthsTop = carrierStrengths.slice(0, Math.ceil(carrierStrengths.length / 2));
      this.carrierStrengthsBottom = carrierStrengths.slice(Math.ceil(carrierStrengths.length / 2));
      this.insurerGroups = Array.from(new Set(this.ratings.map(x => x.insurerGroup)));
      this.loading = false;
    },
    hydrateCarriers() {
      this.carriers = this.ratings.flatMap((group) => group.carriers);
    },
    applyFilters() {
      this.hydrateCarriers();
      if (this.selectedInsurers.length > 0) {
        this.carriers = this.carriers.filter(c => c.insurerGroup === 'All' || this.selectedInsurers.includes(c.insurerGroup));
      }
      if (this.selectedCarrierStrengths.length > 0) {
        this.carriers = this.carriers.filter(c => this.selectedCarrierStrengths.includes(c.financialStrengthRating));
      }

      if (this.selectedCarrierSizes.length > 0) {
       this.carriers = this.carriers.filter(c => c.sizeCategory !== '' && this.selectedCarrierSizes.includes(c.sizeCategory));
      }
    },
    formatDate(date: string | undefined): string {
      if (!date) return '';
      return DateTime.fromISO(date, { zone: 'utc' }).toLocaleString()
    },
    resetFileUpload() {
      this.uploadFileName = '';
      (this.$refs.fileUpload as HTMLInputElement).value = '';
      this.uploadSuccess = null;
      this.uploadFileName = '';
      this.uploadErrors = [];
    },
    dismissUpload() {
      if (this.uploadSuccess) {
        this.refreshData();
      }
      this.resetFileUpload();
      this.showUploadModal = false;
    },
    async confirmUpload() {
      const fileInput = this.$refs.fileUpload as HTMLInputElement;
      const file = fileInput.files?.[0];

      if (file) {
        this.uploadSuccess = null;
        this.uploadErrors = [];
        this.uploading = true;

        try {
          await carrierService.uploadAMBestRatings(file);
          this.uploadSuccess = true;
        } catch (e) {
          const err = e as Error;
          if (err.cause) {
            this.uploadErrors = (err.cause as { errors?: string[] }).errors ?? [];
          }
          this.uploadSuccess = false;
        }

        this.uploading = false;
      }

      fileInput.value = '';
    },
    uploadFileChanged(e: Event) {
      const fileInput = e.target as HTMLInputElement;
      this.uploadFileName = fileInput.value;
      this.uploadSuccess = null;
      this.uploadErrors = [];
    }
  },
  computed: {
    isAdmin(): boolean {
      return IsUserAnAdmin(store?.state.user);
    },
    isFileSelectedForUpload(): boolean {
      return this.uploadFileName.trim() !== '';
    },
    groupedCarriers(): GroupedCarriers[] {
      return this.filteredCarriers.reduce((acc, curr) => {
        const group = acc.find(g => g.groupName === curr.insurerGroup);
        if (group) {
          group.carriers.push(curr);
        } else {
          acc.push({ groupName: curr.insurerGroup, carriers: [curr] });
        }
        return acc;
      }, [] as GroupedCarriers[]);
    },
    filteredCarriers(): CarrierRating[] {
      return this.carriers.filter(c => c.issuingCompanyName.toLowerCase().includes(this.searchText.toLowerCase()));
    }
  },
  created(): Promise<void> {
    return this.refreshData();
  }
};
</script>

